import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

import type { PendingMeasurementSave } from './handleWinchMqttMessage';
import { showWinchToast } from './winchMqttToasts';

/** MQTT command replies are usually fast; fail before the 60s status poll. */
export const SET_POSITION_ACK_TIMEOUT_MS = 10_000;

export function useSetPositionAck(input: {
    isConnected: boolean;
    tRef: MutableRefObject<(key: string) => string>;
    pendingMeasurementRef: MutableRefObject<PendingMeasurementSave | null>;
}): {
    setPositionPending: boolean;
    setPositionFailed: boolean;
    beginSetPositionPending: () => void;
    settleSetPosition: (failed: boolean) => void;
    resetSetPositionAck: () => void;
} {
    const { isConnected, tRef, pendingMeasurementRef } = input;
    const [setPositionPending, setSetPositionPending] = useState(false);
    const [setPositionFailed, setSetPositionFailed] = useState(false);
    const timeoutRef = useRef<number | null>(null);
    const pendingRef = useRef(false);

    const clearAckTimeout = useCallback((): void => {
        if (timeoutRef.current != null) {
            window.clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const applySettle = useCallback(
        (failed: boolean): void => {
            clearAckTimeout();
            pendingRef.current = false;
            setSetPositionPending(false);
            setSetPositionFailed(failed);
        },
        [clearAckTimeout]
    );

    const settleSetPosition = useCallback(
        (failed: boolean): void => {
            applySettle(failed);
        },
        [applySettle]
    );

    const failSetPosition = useCallback((): void => {
        if (!pendingRef.current) {
            return;
        }
        pendingMeasurementRef.current = null;
        applySettle(true);
        showWinchToast(
            tRef.current('winchControl.toast.positionSaveError'),
            'error',
            'winch-setposition-error'
        );
    }, [applySettle, pendingMeasurementRef, tRef]);

    const beginSetPositionPending = useCallback((): void => {
        clearAckTimeout();
        pendingRef.current = true;
        setSetPositionFailed(false);
        setSetPositionPending(true);
        timeoutRef.current = window.setTimeout(() => {
            timeoutRef.current = null;
            failSetPosition();
        }, SET_POSITION_ACK_TIMEOUT_MS);
    }, [clearAckTimeout, failSetPosition]);

    const resetSetPositionAck = useCallback((): void => {
        clearAckTimeout();
        pendingRef.current = false;
        pendingMeasurementRef.current = null;
        setSetPositionPending(false);
        setSetPositionFailed(false);
    }, [clearAckTimeout, pendingMeasurementRef]);

    useEffect(() => {
        if (!setPositionPending || isConnected) {
            return;
        }
        failSetPosition();
    }, [failSetPosition, isConnected, setPositionPending]);

    useEffect(() => () => clearAckTimeout(), [clearAckTimeout]);

    return {
        setPositionPending,
        setPositionFailed,
        beginSetPositionPending,
        settleSetPosition,
        resetSetPositionAck,
    };
}
